import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import {
  API,
  ClientCullDTO,
  ContentCullDTO,
  CullWithAtomsDTO,
  FilterableInventoryRequestDTO,
  GeoCullDTO,
  InventoryAggregationDTO,
  LocalDateTimeRangeDTO,
  PlayerCullDTO,
  TimeCullDTO,
} from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { DateTime } from 'luxon';
import { SbLocalDateTime } from '../../../services/api/api-date-time';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { InventoryListComponent } from '../../../pages/inventory/inventory-list/inventory-list.component';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-filter-preview',
  templateUrl: './sb-filter-preview.component.html',
  styleUrls: ['./sb-filter-preview.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class SbFilterPreviewComponent implements OnInit {
  id: number;

  _filter: CullWithAtomsDTO;

  get filter(): CullWithAtomsDTO {
    return this._filter;
  }

  @Input() set filter(value: CullWithAtomsDTO) {
    this._filter = value;
  }

  @Input() isInModalContext;

  fetchCull: CullWithAtomsDTO;

  loading = true;

  dateTimeRanges: LocalDateTimeRangeDTO[];

  constructor(
    private readonly api: API,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly translate: TranslateService,
    public readonly modalService: ModalService,
    private readonly modalNavigation: ModalNavigationService,
    private readonly destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    this.renderPreview();
  }

  renderPreview() {
    switch (this.filter.type) {
      case 'TimeCull':
        // this.fetch({ month: DateTime.utc().month, year: DateTime.utc().year });
        this.checkForUpdate({ month: DateTime.utc().month, year: DateTime.utc().year });
        break;
      case 'GeoCull':
      case 'ClientCull':
      case 'ContentCull':
      case 'PlayerCull':
        // this.fetch(null);
        this.checkForUpdate(null);
        break;
      default:
        this.loading = false;
        break;
    }
  }

  fetch(e?: { month: number; year: number }) {
    const nuStart = new SbLocalDateTime(DateTime.utc());
    const nuEnd = new SbLocalDateTime(DateTime.utc());

    if (e) {
      nuStart.value = nuStart.value.set({ year: e.year, month: e.month }).startOf('day');
      nuEnd.value = nuEnd.value.set({ year: e.year, month: e.month }).endOf('day');

      nuStart.encodeMillis = true;
      nuEnd.encodeMillis = true;
    }

    let obs: Observable<CullWithAtomsDTO | InventoryAggregationDTO[]> = null;
    if (this.filter) {
      switch (this.filter.type) {
        case 'TimeCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_updateTimeCullPreview(this.filter as TimeCullDTO, {
              displayFrom: nuStart,
              displayTill: nuEnd,
            });
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createTimeCullCompanyScopedPreview(this.filter.company.id, this.filter as TimeCullDTO, {
              displayFrom: nuStart,
              displayTill: nuEnd,
            });
          } else {
            obs = this.api.cs_Cull_createTimeCullClientScopedPreview(this.filter as TimeCullDTO, {
              displayFrom: nuStart,
              displayTill: nuEnd,
            });
          }
          break;
        case 'PlayerCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_updatePlayerCullPreview(this.filter as PlayerCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createPlayerCullCompanyScopedPreview(this.filter.company.id, this.filter as PlayerCullDTO);
          } else {
            obs = this.api.cs_Cull_createPlayerCullClientScopedPreview(this.filter as PlayerCullDTO);
          }
          break;
        case 'GeoCull':
          // TODO change after Backend functionality is implemented
          // const filterEle: FilterableInventoryRequestDTO = {
          //   filters: null,
          //   whitelistPolygons: null,
          //   blacklistPolygons: null,
          //   playerCulls: null,
          //   geoCulls: this.filter?.id ? [this.filter as GeoCullDTO] : null,
          //   descriptorId: null,
          // };
          // obs = this.api.ds_rm_Inventory_aggregation([filterEle]);
          if (this.filter.id) {
            obs = this.api.cs_Cull_updateGeoCullPreview(this.filter as GeoCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createGeoCullCompanyScopedPreview(this.filter.company.id, this.filter as GeoCullDTO);
          } else {
            obs = this.api.cs_Cull_createGeoCullClientScopedPreview(this.filter as GeoCullDTO);
          }
          break;
        case 'ClientCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_updateClientCullPreview(this.filter as ClientCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createClientCullCompanyScopedPreview(this.filter.company.id, this.filter as ClientCullDTO);
          } else {
            obs = this.api.cs_Cull_createClientCullClientScopedPreview(this.filter as ClientCullDTO);
          }
          break;
        case 'ContentCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_updateContentCullPreview(this.filter as ContentCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createContentCullCompanyScopedPreview(this.filter.company.id, this.filter as ContentCullDTO);
          } else {
            obs = this.api.cs_Cull_createContentCullClientScopedPreview(this.filter as ContentCullDTO);
          }
          break;
      }
    }

    if (!obs) {
      this.loading = false;
      return;
    }
    obs
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((cull: CullWithAtomsDTO) => {
        this.fetchCull = cull;
        switch (this.filter.type) {
          case 'TimeCull': {
            const timeCull: TimeCullDTO = this.fetchCull as TimeCullDTO;
            this.dateTimeRanges = timeCull.validTimeRanges;
            break;
          }
          /*
          case 'GeoCull': {
            this.fetchCull['aggregation'] = cull[0];
            break;
          }
          */
        }
      });
  }

  openInventory(type: string, pSelection: number) {
    if (this.filter.id) {
      if (this.isInModalContext) {
        this.modalNavigation.navigate(InventoryListComponent, {
          selection: pSelection,
          playerCull: type === 'PlayerCull' ? this.filter : null,
          geoCull: type === 'GeoCull' ? this.filter : null,
        });
      } else {
        this.modalService
          .showNavigationModal({
            entryComponent: {
              value: InventoryListComponent,
              data: {
                selection: pSelection,
                playerCull: type === 'PlayerCull' ? this.filter : null,
                geoCull: type === 'GeoCull' ? this.filter : null,
              },
            },
            title: null,
            modalOptions: { windowClass: 'bigXL' },
          })
          .then()
          .catch(() => {
            // needs to be added due to closing of modals being interpreted as rejection
          });
      }
    }
  }

  getText(key: string) {
    if (this.fetchCull?.['aggregation']?.[key] != null) {
      const count: number = +this.fetchCull['aggregation'][key];
      const ending: string =
        count === 1
          ? this.translate.instant('lang.globals.' + key.substring(0, key.length - 1))
          : this.translate.instant('lang.globals.' + key);
      return count + ' ' + ending;
    }
    return this.translate.instant('lang.globals.noData');
  }

  protected checkForUpdate(e?: { month: number; year: number }) {
    const nuStart = new SbLocalDateTime(DateTime.utc());
    const nuEnd = new SbLocalDateTime(DateTime.utc());

    if (e) {
      nuStart.value = nuStart.value.set({ year: e.year, month: e.month }).startOf('month');
      nuEnd.value = nuEnd.value.set({ year: e.year, month: e.month }).endOf('month');

      nuStart.encodeMillis = true;
      nuEnd.encodeMillis = true;
    }

    if (this.filter.cached && DateTime.local().diff(this.filter.cached.value, 'minute').minutes < 2) {
      this.fetchCull = this.filter;
      this.loading = false;
      return;
    }

    let obs: Observable<CullWithAtomsDTO | InventoryAggregationDTO[]> = null;
    if (this.filter) {
      // this.loading = true;
      switch (this.filter.type) {
        case 'TimeCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_refreshTimeCull(this.filter.id);
            /*
            obs = this.api.cs_Cull_updateTimeCullPreview(this.filter as TimeCullDTO, {
              displayFrom: nuStart,
              displayTill: nuEnd,
            });

           */
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createTimeCullCompanyScopedPreview(this.filter.company.id, this.filter as TimeCullDTO, {
              displayFrom: nuStart,
              displayTill: nuEnd,
            });
          } else {
            obs = this.api.cs_Cull_createTimeCullClientScopedPreview(this.filter as TimeCullDTO, {
              displayFrom: nuStart,
              displayTill: nuEnd,
            });
          }
          break;
        case 'PlayerCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_refreshPlayerCull(this.filter.id);
            // obs = this.api.cs_Cull_updatePlayerCullPreview(this.filter as PlayerCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createPlayerCullCompanyScopedPreview(this.filter.company.id, this.filter as PlayerCullDTO);
          } else {
            obs = this.api.cs_Cull_createPlayerCullClientScopedPreview(this.filter as PlayerCullDTO);
          }
          break;
        case 'GeoCull':
          const filterEle: FilterableInventoryRequestDTO = {
            filters: null,
            whitelistPolygons: null,
            blacklistPolygons: null,
            playerCulls: null,
            geoCulls: this.filter?.id ? [this.filter as GeoCullDTO] : null,
            descriptorId: null,
          };
          // obs = this.api.ds_rm_Inventory_aggregation([filterEle]);
          // TODO change after Backend functionality is implemented
          if (this.filter.id) {
            obs = this.api.cs_Cull_refreshGeoCull(this.filter.id);
            // obs = this.api.cs_Cull_updateGeoCullPreview(this.filter as GeoCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createGeoCullCompanyScopedPreview(this.filter.company.id, this.filter as GeoCullDTO);
          } else {
            obs = this.api.cs_Cull_createGeoCullClientScopedPreview(this.filter as GeoCullDTO);
          }
          break;
        case 'ClientCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_refreshClientCull(this.filter.id);
            // obs = this.api.cs_Cull_updateClientCullPreview(this.filter as ClientCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createClientCullCompanyScopedPreview(this.filter.company.id, this.filter as ClientCullDTO);
          } else {
            obs = this.api.cs_Cull_createClientCullClientScopedPreview(this.filter as ClientCullDTO);
          }
          break;
        case 'ContentCull':
          if (this.filter.id) {
            obs = this.api.cs_Cull_refreshContentCull(this.filter.id);
            // obs = this.api.cs_Cull_updateContentCullPreview(this.filter as ContentCullDTO);
          } else if (this.filter.company) {
            obs = this.api.cs_Cull_createContentCullCompanyScopedPreview(this.filter.company.id, this.filter as ContentCullDTO);
          } else {
            obs = this.api.cs_Cull_createContentCullClientScopedPreview(this.filter as ContentCullDTO);
          }
          break;
      }
    }

    if (!obs) {
      this.loading = false;
      return;
    }
    obs
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((cull: CullWithAtomsDTO) => {
        this.fetchCull = cull;
        switch (this.filter.type) {
          case 'TimeCull': {
            const timeCull: TimeCullDTO = this.fetchCull as TimeCullDTO;
            this.dateTimeRanges = timeCull.validTimeRanges;
            break;
          }
          /*
          case 'GeoCull': {
            this.fetchCull['aggregation'] = cull[0];
            break;
          }
          */
        }
      });
  }
}
